import React from 'react';
import { motion } from 'framer-motion';
import { useData } from '../../contexts/DataContext';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiUser, FiCalendar, FiClock, FiExternalLink, FiMail, FiPhone, FiHome } = FiIcons;

const RecentActivity = () => {
  const { guestPages, properties } = useData();
  const navigate = useNavigate();

  const recentPages = guestPages
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, 5);

  const getPropertyName = (propertyId) => {
    const property = properties.find(p => p.id === propertyId);
    return property?.name || 'Unknown Property';
  };

  const handleActivityClick = (page) => {
    // Navigate to guest page edit with query parameter
    navigate(`/guest-pages?edit=${page.id}`);
  };

  const handleQuickActions = (page, action) => {
    const portalUrl = `${window.location.origin}#/portal/${page.slug}`;
    switch(action) {
      case 'view':
        window.open(portalUrl, '_blank');
        break;
      case 'email':
        if (page.guestEmail) {
          const property = properties.find(p => p.id === page.propertyId);
          const subject = `Your Stay Information - ${property?.name}`;
          const body = `Hi ${page.guestName},\n\nYour guest portal is ready! Access your stay information using the link below:\n\n${portalUrl}\n\nWhen prompted, enter the last 4 digits of your phone number: ${page.lastFourDigits}\n\nBest regards,\n${property?.name} Team`;
          window.open(`mailto:${page.guestEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
        }
        break;
      case 'copy':
        navigator.clipboard.writeText(portalUrl);
        // Would show toast here
        break;
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      className="bg-white rounded-xl shadow-sm p-6 border border-gray-100"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
        <button 
          onClick={() => navigate('/guest-pages')}
          className="text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
        >
          View All
        </button>
      </div>

      <div className="space-y-3">
        {recentPages.length > 0 ? (
          recentPages.map((page, index) => (
            <motion.div
              key={page.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="group"
            >
              {/* Main Activity Tile - Clickable */}
              <motion.button
                onClick={() => handleActivityClick(page)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full text-left p-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition-all duration-200 group border border-transparent hover:border-blue-200"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    {/* Status Indicator */}
                    <div className={`p-2 rounded-full flex-shrink-0 ${page.status === 'active' ? 'bg-green-100 group-hover:bg-green-200' : 'bg-gray-100 group-hover:bg-gray-200'} transition-colors duration-200`}>
                      <SafeIcon icon={FiUser} className={`text-sm ${page.status === 'active' ? 'text-green-600' : 'text-gray-600'}`} />
                    </div>
                    
                    {/* Guest Info */}
                    <div className="flex-1 min-w-0 pr-3">
                      <p className="text-sm font-medium text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                        {page.guestName}
                      </p>
                      <div className="flex items-center space-x-2 text-xs text-gray-500 mt-1">
                        {/* Enhanced Property Display in Activity */}
                        <span className="flex items-center bg-white px-1.5 py-0.5 rounded border border-gray-200 truncate max-w-[120px]">
                          <SafeIcon icon={FiHome} className="mr-1 text-[10px]" />
                          <span className="truncate">{getPropertyName(page.propertyId)}</span>
                        </span>
                        <span>•</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${page.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                          {page.status}
                        </span>
                      </div>
                    </div>

                    {/* Date and External Link Indicator */}
                    <div className="flex flex-col items-end space-y-1">
                      <p className="text-xs text-gray-400 whitespace-nowrap">
                        {format(new Date(page.createdAt), 'MMM d')}
                      </p>
                      <motion.div 
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.2 }}
                        className="flex items-center space-x-1 text-gray-400 group-hover:text-blue-600 transition-colors"
                      >
                        <SafeIcon icon={FiExternalLink} className="text-xs" />
                        <span className="text-xs">Edit</span>
                      </motion.div>
                    </div>
                  </div>
                </div>
              </motion.button>
              
              {/* Quick Actions Bar - Appears on hover */}
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                whileHover={{ opacity: 1, height: 'auto' }}
                className="overflow-hidden"
              >
                <div className="flex items-center justify-between pl-12 pr-2 pt-1">
                  <div className="flex items-center space-x-1">
                    {/* View Portal */}
                    <motion.button
                      onClick={(e) => { e.stopPropagation(); handleQuickActions(page, 'view'); }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors duration-200"
                      title="View portal"
                    >
                      <SafeIcon icon={FiExternalLink} className="text-sm" />
                    </motion.button>
                    {/* Send Email */}
                    {page.guestEmail && (
                      <motion.button
                        onClick={(e) => { e.stopPropagation(); handleQuickActions(page, 'email'); }}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors duration-200"
                        title="Send email"
                      >
                        <SafeIcon icon={FiMail} className="text-sm" />
                      </motion.button>
                    )}
                    {/* Copy Link */}
                    <motion.button
                      onClick={(e) => { e.stopPropagation(); handleQuickActions(page, 'copy'); }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                      title="Copy portal link"
                    >
                      <FiCalendar className="text-sm" />
                    </motion.button>
                  </div>
                  
                  {/* Additional Info */}
                  <div className="flex items-center space-x-3 text-xs text-gray-400">
                    {page.checkInDate && (
                      <div className="flex items-center space-x-1">
                        <FiCalendar className="text-xs" />
                        <span>{format(new Date(page.checkInDate), 'MMM d')}</span>
                      </div>
                    )}
                    {page.phone && (
                      <div className="flex items-center space-x-1">
                        <FiPhone className="text-xs" />
                        <span>{page.phone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')}</span>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          ))
        ) : (
          <div className="text-center py-8">
            <SafeIcon icon={FiClock} className="text-gray-400 text-3xl mx-auto mb-2" />
            <p className="text-gray-500">No recent activity</p>
            <motion.button 
              onClick={() => navigate('/guest-pages')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="mt-4 text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Create your first guest page
            </motion.button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default RecentActivity;